// Office widget: live map of today's crew clock-in locations.
// Green pin = currently on site, grey = signed out. Hideable via ui-prefs.
import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { MapPin, RefreshCw } from "lucide-react";

function pinIcon(name, onSite) {
  const colour = onSite ? "#22C55E" : "#71717A";
  const first = (name || "?").split(" ")[0];
  const html = `
    <div style="display:flex;align-items:center;gap:4px;font-family:'JetBrains Mono',ui-monospace,monospace;
                font-weight:700;font-size:11px;pointer-events:none;transform:translate(-9px,-9px);">
      <div style="width:14px;height:14px;border-radius:50%;background:${colour};
                  border:3px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.35);"></div>
      <div style="background:#09090B;color:#fff;padding:1px 5px;white-space:nowrap;
                  letter-spacing:.05em;box-shadow:0 1px 2px rgba(0,0,0,.4);">${first}</div>
    </div>`;
  return L.divIcon({ html, className: "crew-marker", iconSize: [0, 0], iconAnchor: [0, 0] });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 13);
    else map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
  }, [map, points]);
  return null;
}

export function CrewLiveMap() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tileError, setTileError] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/clock/today");
      setData(d);
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const withCoords = useMemo(
    () => (data?.signins || []).filter((s) => s.lat != null && s.lng != null),
    [data]
  );
  const points = useMemo(() => withCoords.map((s) => [s.lat, s.lng]), [withCoords]);
  const onSiteCount = (data?.signins || []).filter((s) => s.on_site).length;

  if (!data) return null;

  return (
    <div className="hard-card bg-white dark:bg-[#151C27] overflow-hidden" data-testid="crew-live-map">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-[#FF5F15]" />
          <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-zinc-500">Crew on the ground · Today</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[11px] font-mono font-bold" data-testid="crew-map-on-site-count">
            <span className={`w-2 h-2 rounded-full ${onSiteCount ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"}`} />
            {onSiteCount} on site now
          </span>
          <button onClick={() => { setTileError(false); load(); }} disabled={busy} className="text-zinc-400 hover:text-zinc-900 dark:hover:text-white" data-testid="crew-map-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {withCoords.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-zinc-400 font-mono uppercase tracking-wider" data-testid="crew-map-empty">
          No GPS clock-ins yet today — pins appear as the crew taps "Clock in"
        </div>
      ) : (
        <div className="h-72 relative z-0">
          {tileError ? (
            <div role="status" className="h-full flex items-center justify-center px-4 text-center text-sm text-zinc-500">
              Map temporarily unavailable. Crew details are shown below. Use refresh to retry.
            </div>
          ) : <MapContainer center={points[0]} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            {/* Reuse Fieldlink's existing Esri imagery basemap. */}
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                       attribution='Tiles &copy; Esri'
                       eventHandlers={{ tileerror: () => setTileError(true) }} />
            <FitBounds points={points} />
            {withCoords.map((s) => (
              <Marker key={s.id} position={[s.lat, s.lng]} icon={pinIcon(s.name, s.on_site)}>
                <Popup>
                  <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                    <b>{s.name}</b><br />
                    {s.job_title || "No job linked"}<br />
                    In {s.in_time}{s.out_time ? ` · Out ${s.out_time}` : " · still on site"}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>}
        </div>
      )}

      {(data.signins || []).length > 0 && (
        <div className="px-4 py-2 border-t border-zinc-200 dark:border-zinc-800 flex flex-wrap gap-x-4 gap-y-1">
          {(data.signins || []).slice(0, 10).map((s) => (
            <span key={s.id} className="text-[10px] font-mono text-zinc-500" data-testid={`crew-map-row-${s.id}`}>
              <span className={s.on_site ? "text-emerald-600 font-bold" : ""}>{s.name}</span> {s.in_time}{s.out_time ? `–${s.out_time}` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
